import type { ClanEffectivePermissions } from "@acme/validators-shared/routers/clan/effectivePermissions";
import type { EntityDetailTabRoute } from "~/common/components/entity-detail-tabs.js";
import type { FileRoutesByTo } from "~/routeTree.gen.js";

export const CLAN_TAB_ROUTES = [
  { value: "dashboard", to: "/my-clans/$tag", labelKey: "app.clanDetail.tabs.dashboard", end: true },
  { value: "players", to: "/my-clans/$tag/players", labelKey: "app.clanDetail.tabs.players", end: false },
  {
    value: "player-search",
    to: "/my-clans/$tag/player-search/filters",
    labelKey: "app.clanDetail.tabs.playerSearch",
    end: false,
  },
  {
    value: "social-media",
    to: "/my-clans/$tag/social-media",
    labelKey: "app.clanDetail.tabs.socialMedia",
    end: false,
  },
  {
    value: "notifications",
    to: "/my-clans/$tag/notifications",
    labelKey: "app.clanDetail.tabs.notifications",
    end: false,
  },
] as const satisfies readonly EntityDetailTabRoute[];

export type ClanTabValue = (typeof CLAN_TAB_ROUTES)[number]["value"];

export const canReadClanPlayerSearch = (permissions: ClanEffectivePermissions): boolean =>
  permissions.admin ||
  permissions.readClanSearchFilter ||
  permissions.readClanApiSearchFilter ||
  permissions.readClanPersonalSearchFilter;

export const canReadClanPlayerSearchResults = (permissions: ClanEffectivePermissions): boolean =>
  permissions.admin || permissions.readMatchs;

export const isClanTabVisible = (
  tabValue: ClanTabValue,
  permissions: ClanEffectivePermissions
): boolean => {
  switch (tabValue) {
    case "dashboard":
    case "players":
    case "social-media":
    case "notifications":
      return permissions.canAccessClan;
    case "player-search":
      return canReadClanPlayerSearch(permissions) || canReadClanPlayerSearchResults(permissions);
    default:
      return false;
  }
};

export const getVisibleClanTabs = (permissions: ClanEffectivePermissions) =>
  CLAN_TAB_ROUTES.filter((tab) => isClanTabVisible(tab.value, permissions));

export const getClanActiveTab = (pathname: string, tag: string): ClanTabValue => {
  if (
    pathname.includes("/player-search") ||
    pathname.includes("/search-filters") ||
    pathname.includes("/search-filter") ||
    pathname.includes("/matches")
  ) {
    return "player-search";
  }

  for (let i = CLAN_TAB_ROUTES.length - 1; i >= 0; i--) {
    const { value, to, end } = CLAN_TAB_ROUTES[i];
    const path = to.replace("$tag", tag);
    if (end ? pathname === path || pathname === `${path}/` : pathname.startsWith(path)) {
      return value;
    }
  }

  return "dashboard";
};

export const getFirstVisibleClanTabRoute = (
  permissions: ClanEffectivePermissions
): EntityDetailTabRoute["to"] => {
  const visibleTabs = getVisibleClanTabs(permissions);
  return visibleTabs[0]?.to ?? ("/my-clans/$tag");
};

export const getFirstVisiblePlayerSearchSubTabRoute = (
  permissions: ClanEffectivePermissions
): keyof FileRoutesByTo => {
  if (canReadClanPlayerSearch(permissions)) {
    return "/my-clans/$tag/player-search/filters";
  }
  if (canReadClanPlayerSearchResults(permissions)) {
    return "/my-clans/$tag/player-search/results";
  }
  return "/my-clans/$tag/player-search/filters";
};
